/**
 * MyDay.tsx — Home tabs H-3b-2 (spec §4; the H-3b recon rulings, design note
 * §42): the My day tab's body — the prioritised list on the left, the Today /
 * This week / 5-minute clean-up rail on the right. Every rule and sentence
 * lives in myDayView.ts; this renders them. State (done, snoozed, the filter)
 * is held by SalesDashboard, because the shared header's lede and the
 * switch's badge read the same open count.
 *
 * P1: colors flow from src/theme/tokens.ts (canonical design system).
 */
import { Phone, Mail, Users, FileText, CheckSquare, Briefcase, Inbox, Check } from 'lucide-react'
import type { SalesActivity, SalesContact } from '../../types'
import { activityType, accent, warning } from '../../../../theme/tokens'
import {
  GROUP_ORDER, GROUP_CAPTION, hasOverdue, FILTERS, filterCounts, myDayHeadline, estimateLine, snoozedLine, agenda, nextDayTile, returningLine,
  weekCounts, weekNote, cleanUp, unlinkedRow, malformedRow, unauthoredRow, dayLabel,
  type MyDayList, type MyDayItem, type MyDayFilter, type MyDayTile,
} from './myDayView'
import { DismissedRows, DismissedCapsule } from './DismissedRows'
import { useWriteAccess } from '../../../../lib/writeAccess'

// Spec §4.1's type tiles: meeting indigo, call green, email amber, a deal or
// a lead the accent — each a token pair, never a raw colour.
const TILE: Record<MyDayTile, { icon: React.ReactNode; color: string; bg: string }> = {
  meeting: { icon: <Users size={16} />, color: activityType.meeting.color, bg: activityType.meeting.bg },
  call: { icon: <Phone size={16} />, color: activityType.call.color, bg: activityType.call.bg },
  email: { icon: <Mail size={16} />, color: warning.DEFAULT, bg: warning.bg },
  note: { icon: <FileText size={16} />, color: activityType.note.color, bg: activityType.note.bg },
  task: { icon: <CheckSquare size={16} />, color: activityType.task.color, bg: activityType.task.bg },
  deal: { icon: <Briefcase size={16} />, color: accent.DEFAULT, bg: accent.soft },
  lead: { icon: <Inbox size={16} />, color: accent.DEFAULT, bg: accent.soft },
}

const TALLY: { type: 'call' | 'email' | 'meeting'; label: string }[] = [
  { type: 'call', label: 'Calls' }, { type: 'email', label: 'Emails' }, { type: 'meeting', label: 'Meetings' },
]

export interface MyDayProps {
  list: MyDayList
  today: string
  nextWorkingDay: string
  /** The off-day line (myDayView.offDayNote), or null. */
  offDay: string | null
  filter: MyDayFilter
  onFilter: (f: MyDayFilter) => void
  pending: SalesActivity[]
  /** Every activity, any status — null when the read failed. */
  history: SalesActivity[] | null
  historyCapped: boolean
  historyCap: number
  contacts: SalesContact[] | null
  onAction: (item: MyDayItem, el: HTMLElement) => void
  onTick: (item: MyDayItem) => void
  onSnooze: (item: MyDayItem) => void
  onBringBack: (keys: string[]) => void
  onNavigate: (page: string) => void
  /** RULED 2026-10-21: whether the dismissed items are shown, and its switch. */
  showDismissed: boolean
  onToggleDismissed: () => void
}

function Ring({ done, total }: { done: number; total: number }) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0
  return (
    <div className="k-myday-ring" role="img" aria-label={`${done} of ${total} done`} style={{ ['--k-myday-pct' as string]: `${pct}%` }}>
      <span>{done}/{total}</span>
    </div>
  )
}

function Item({ item, done, nwdLabel, onAction, onTick, onSnooze, dismissed = false }: {
  item: MyDayItem; done: boolean; nwdLabel: string
  onAction: MyDayProps['onAction']; onTick: MyDayProps['onTick']; onSnooze: MyDayProps['onSnooze']
  /** A dismissed item shown on request: the capsule, its primary action, no snooze. */
  dismissed?: boolean
}) {
  const tile = TILE[item.tile]
  // Done / Undo (complete, reopen) and Move (a dueAt PUT) are records-level
  // writes (lib/writeAccess): without access the tick and the Move action are
  // not offered. Snooze is device-local and stays.
  const { records } = useWriteAccess()
  const showPrimary = records || item.action.kind !== 'move'
  return (
    <li className={`k-myday-item${done ? ' is-done' : ''}`}>
      {item.activity && !dismissed && records
        ? (
          <button type="button" role="checkbox" aria-checked={done} aria-label={done ? `Undo done: ${item.title}` : `Mark done: ${item.title}`}
            className="k-myday-tick" onClick={() => onTick(item)}>
            {done && <Check size={14} strokeWidth={3} aria-hidden="true" />}
          </button>
        )
        : <span className="k-myday-tick-gap" aria-hidden="true" />}
      <span className="k-myday-tile" style={{ color: tile.color, background: tile.bg }} aria-hidden="true">{tile.icon}</span>
      <div className="k-myday-body">
        <span className="k-myday-title">{item.title}{dismissed && <> <DismissedCapsule /></>}</span>
        <span className="k-myday-why">✦ {item.why}</span>
        <span className="k-myday-meta">{item.meta}</span>
        {!done && (
          <div className="k-myday-actions">
            {showPrimary && <button type="button" className="k-myday-primary" onClick={e => onAction(item, e.currentTarget)}>{item.action.label}</button>}
            {!dismissed && <button type="button" className="k-myday-outline" onClick={() => onSnooze(item)}>Snooze to {nwdLabel}</button>}
          </div>
        )}
      </div>
    </li>
  )
}

export function MyDay(p: MyDayProps) {
  const { list } = p
  const counts = filterCounts(list)
  const shown = list.items.filter(i => p.filter === 'all' || i.category === p.filter)
  const estimate = estimateLine(list.items, list.done)
  const nwdLabel = dayLabel(p.nextWorkingDay)
  const todayRows = agenda(p.pending, p.today)
  const tile = nextDayTile(p.pending, p.nextWorkingDay, list.snoozed)
  const week = p.history ? weekCounts(p.history, p.today) : null
  const clean = cleanUp(p.history, p.contacts)
  const cleanRows = [
    p.history && clean.unlinked > 0 ? { key: 'unlinked', ...unlinkedRow(clean.unlinked), page: 'sales.activities' } : null,
    p.contacts && clean.malformed > 0 ? { key: 'malformed', ...malformedRow(clean.malformed, clean.malformedExamples), page: 'sales.contacts' } : null,
    p.history && clean.unauthored > 0 ? { key: 'unauthored', ...unauthoredRow(clean.unauthored), page: 'sales.activities' } : null,
  ].filter((r): r is NonNullable<typeof r> => r != null)
  const historyNote = p.historyCapped ? `From the first ${p.historyCap} activities only.` : null

  return (
    <div className="k-myday-grid">
      <section className="k-card k-home-card" aria-label="My day list">
        <div className="flex items-center gap-4">
          <Ring done={list.doneCount} total={list.total} />
          <div className="flex flex-col gap-[2px] min-w-0">
            <h2 className="k-home-card-title">{myDayHeadline(list.open, p.today)}</h2>
            {list.open > 0 && (
              <span className="k-home-card-sub">Ordered by what costs you most if it waits.{estimate ? ` ${estimate}` : ''}</span>
            )}
          </div>
        </div>
        {list.total > 0 && (
          <div role="tablist" aria-label="My day filter" className="k-seg self-start">
            {FILTERS.map(f => (
              <button key={f.id} type="button" role="tab" aria-selected={p.filter === f.id} onClick={() => p.onFilter(f.id)}>
                {f.label}<span className="k-home-tab-count">{counts[f.id]}</span>
              </button>
            ))}
          </div>
        )}
        {GROUP_ORDER.map(g => {
          const rows = shown.filter(i => i.group === g)
          if (rows.length === 0) return null
          return (
            <div key={g} className="flex flex-col gap-2">
              <h3 className={`k-myday-caption${g === 'due' ? ' is-due' : ''}`}>{GROUP_CAPTION(g, p.nextWorkingDay, g === 'due' && hasOverdue(list.items, list.done, p.today))}</h3>
              <ul className="k-myday-list">
                {rows.map(i => <Item key={i.key} item={i} done={list.done.has(i.key)} nwdLabel={nwdLabel} onAction={p.onAction} onTick={p.onTick} onSnooze={p.onSnooze} />)}
              </ul>
            </div>
          )
        })}
        {list.snoozed.map(s => (
          <p key={s.until} className="k-myday-snoozed">
            {snoozedLine(s.keys.length, s.until)}{' '}
            <button type="button" className="k-home-link" onClick={() => p.onBringBack(s.keys)}>Bring back</button>
          </p>
        ))}
        {list.dismissed.length > 0 && (
          <DismissedRows count={list.dismissed.length} shown={p.showDismissed} onToggle={p.onToggleDismissed} list="ul">
            {list.dismissed.map(i => <Item key={i.key} item={i} done={false} nwdLabel={nwdLabel} onAction={p.onAction} onTick={p.onTick} onSnooze={p.onSnooze} dismissed />)}
          </DismissedRows>
        )}
      </section>

      <div className="flex flex-col gap-5 min-w-0">
        <section className="k-card k-home-card" aria-label="Today">
          <h2 className="k-home-card-title">Today</h2>
          {todayRows.length === 0
            ? <p className="k-myday-quiet">Nothing is scheduled.</p>
            : (
              <ul className="k-myday-agenda">
                {todayRows.map(r => <li key={r.id}><span className="k-myday-time">{r.time}</span>{r.subject}</li>)}
              </ul>
            )}
          {p.offDay && <p className="k-myday-quiet">{p.offDay}</p>}
          <div className="k-myday-next">
            <span className="k-myday-next-day" aria-hidden="true"><span>{tile.label.slice(0, 3).toUpperCase()}</span><b>{tile.label.split(' ')[1]}</b></span>
            <div className="flex flex-col gap-[2px] min-w-0">
              <span className="k-myday-title">{tile.weekday}</span>
              {tile.planned.slice(0, 3).map(r => <span key={r.id} className="k-myday-meta">{r.time} · {r.subject}</span>)}
              {tile.planned.length > 3 && <span className="k-myday-meta">and {tile.planned.length - 3} more</span>}
              {tile.returning > 0 && <span className="k-myday-meta">{returningLine(tile.returning)}</span>}
              {tile.planned.length === 0 && tile.returning === 0 && <span className="k-myday-meta">Nothing planned yet.</span>}
            </div>
          </div>
        </section>

        <section className="k-card k-home-card" aria-label="This week">
          <h2 className="k-home-card-title">This week</h2>
          {week
            ? (
              <>
                <div className="k-myday-tally">
                  {TALLY.map(t => <div key={t.type}><b>{week.thisWeek[t.type]}</b><span>{t.label}</span></div>)}
                </div>
                <p className="k-myday-quiet">✦ {weekNote(week.lastTotal, week.thisTotal)}</p>
                {historyNote && <p className="k-myday-meta">{historyNote}</p>}
              </>
            )
            : <p className="k-myday-quiet">The activity history couldn't be read.</p>}
        </section>

        <section className="k-card k-home-card" aria-label="5-minute clean-up">
          <h2 className="k-home-card-title">✦ 5-minute clean-up</h2>
          {cleanRows.length === 0
            ? <p className="k-myday-quiet">Nothing to clean up.</p>
            : cleanRows.map(r => (
              <p key={r.key} className="k-myday-clean">
                <button type="button" className="k-home-link" onClick={() => p.onNavigate(r.page)}>{r.count}</button>{r.rest}
              </p>
            ))}
          {historyNote && cleanRows.length > 0 && <p className="k-myday-meta">{historyNote}</p>}
        </section>
      </div>
    </div>
  )
}
